import React, { useState, useEffect, useCallback } from "react";
import Card from "../card/Card";
import Button from "../button/Button";
import { authFetch } from "../../../utils/memorialApi";
import { toast } from "react-toastify";

export function MemorialPrivacySettings({
  memorial,
  onPrivacyUpdate,
  onNotificationUpdate,
  onRefresh,
}) {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

  const handleModerationToggle = async (checked) => {
    try {
      const res = await authFetch(`${backendUrl}/api/memorials/${memorial._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          moderation: { requireApproval: checked },
        }),
      });
      if (res.ok) {
        toast.success(checked ? "Tribute review enabled" : "Tributes publish immediately");
        if (onRefresh) onRefresh();
      } else {
        toast.error("Could not update moderation settings");
      }
    } catch (e) {
      toast.error("Could not update moderation settings");
    }
  };

  return (
    <Card Width="75vw">
      <p className="dialog-header">Privacy & Notifications</p>
      <div className="dialog-content">
        <div className="privacy-checkbox-section">
          <p className="youremail">Privacy Options:</p>
          <div className="privacy-checkbox-group-1">
            <div className="radio-container">
              <label className="custom-radio">
                <input
                  type="radio"
                  name="privacy"
                  value="onlyme"
                  checked={memorial?.privacy?.onlyme}
                  onChange={() => onPrivacyUpdate(true)}
                />
                <span className="radiomark"></span>
                Visible only to me
              </label>
              <p>Choose this option if you do not want the memorial to be visible to others at this time.</p>
            </div>
            <div className="radio-container">
              <label className="custom-radio">
                <input
                  type="radio"
                  name="privacy"
                  value="public"
                  checked={!memorial?.privacy?.onlyme}
                  onChange={() => onPrivacyUpdate(false)}
                />
                <span className="radiomark"></span>
                All visitors can view and contribute
              </label>
              <p>Recommended for most memorials. This option allows easy access to the website and facilitates collaboration.</p>
            </div>
          </div>
        </div>

        <div className="notification-section" style={{ marginTop: 24 }}>
          <p className="youremail">Guest tributes:</p>
          <div className="checkbox-container">
            <label className="custom-checkbox">
              <input
                type="checkbox"
                checked={memorial?.moderation?.requireApproval === true}
                onChange={(e) => handleModerationToggle(e.target.checked)}
              />
              <span className="checkmark"></span>
              Review guest tributes before they appear
            </label>
            <p style={{ marginTop: 8, fontSize: "0.9rem" }}>
              When enabled, visitor tributes stay pending until you approve them.
            </p>
          </div>
        </div>

        <div className="notification-section">
          <p className="youremail">Default Notification:</p>
          <div className="privacy-checkbox-group-2">
            <div className="checkbox-container">
              <label className="custom-checkbox">
                <input
                  type="checkbox"
                  checked={memorial?.notification?.birthday || false}
                  onChange={(e) => onNotificationUpdate("birthday", e.target.checked)}
                />
                <span className="checkmark"></span>
                On {memorial?.firstName}&apos;s Birthday
              </label>
            </div>
            <div className="checkbox-container">
              <label className="custom-checkbox">
                <input
                  type="checkbox"
                  checked={memorial?.notification?.passed || false}
                  onChange={(e) => onNotificationUpdate("passed", e.target.checked)}
                />
                <span className="checkmark"></span>
                On day the loved one passed
              </label>
            </div>
            <div className="checkbox-container">
              <label className="custom-checkbox">
                <input
                  type="checkbox"
                  checked={memorial?.notification?.post || false}
                  onChange={(e) => onNotificationUpdate("post", e.target.checked)}
                />
                <span className="checkmark"></span>
                On new posts from other guests
              </label>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}

export function MemorialPendingTributes({
  memorial,
  pendingTributes = [],
  selectedTheme,
  paragraphStyle,
  backendUrl,
  memorialId,
  onRefresh,
}) {
  const textColor = selectedTheme?.textColor || "#5c5470";
  const [moderateLoading, setModerateLoading] = useState(null);

  const pendingList =
    pendingTributes.length > 0
      ? pendingTributes
      : (memorial?.about?.tributes || []).filter(
          (t) => (t.status || "approved") === "pending"
        );

  const handleModerateTribute = async (tributeId, action) => {
    setModerateLoading(tributeId);
    try {
      const res = await authFetch(
        `${backendUrl}/api/memorials/${memorialId}/tributes/${tributeId}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action }),
        }
      );
      if (res.ok) {
        toast.success(action === "approve" ? "Tribute approved" : "Tribute rejected");
        onRefresh();
      } else {
        toast.error("Could not update tribute");
      }
    } catch (e) {
      toast.error("Could not update tribute");
    } finally {
      setModerateLoading(null);
    }
  };

  return (
    <Card Width="90%">
      <p className="manage-title2" style={{ color: textColor }}>
        Pending tributes {pendingList.length > 0 ? `(${pendingList.length})` : ""}
      </p>
      {pendingList.length === 0 ? (
        <p style={paragraphStyle}>No tributes awaiting review.</p>
      ) : (
        pendingList.map((tribute) => (
          <div key={tribute._id} className="manage-box" style={{ marginBottom: 16, padding: 16 }}>
            <p style={{ ...paragraphStyle, fontWeight: 600 }}>
              {tribute.user?.name || tribute.user?.email || "Guest"}
            </p>
            <p style={paragraphStyle}>{tribute.description}</p>
            <div className="manage-btn-section">
              <button
                type="button"
                className="pending-btn"
                disabled={moderateLoading === tribute._id}
                onClick={() => handleModerateTribute(tribute._id, "approve")}
              >
                Approve
              </button>
              <button
                type="button"
                className="visited-btn"
                disabled={moderateLoading === tribute._id}
                onClick={() => handleModerateTribute(tribute._id, "reject")}
              >
                Reject
              </button>
            </div>
          </div>
        ))
      )}
    </Card>
  );
}

export function MemorialTransferSettings({ memorial, selectedTheme, backendUrl, memorialId }) {
  const textColor = selectedTheme?.textColor || "#5c5470";
  const [transferEmail, setTransferEmail] = useState("");
  const [transferLoading, setTransferLoading] = useState(false);
  const [pendingTransfer, setPendingTransfer] = useState(null);
  const [pendingLoading, setPendingLoading] = useState(true);
  const [cancelLoading, setCancelLoading] = useState(false);

  const fetchPendingTransfer = useCallback(async () => {
    if (!memorialId) return;
    setPendingLoading(true);
    try {
      const res = await authFetch(
        `${backendUrl}/api/memorials/${memorialId}/transfer/pending`
      );
      if (res.ok) {
        const data = await res.json();
        setPendingTransfer(data.transfer || null);
      }
    } catch (e) {
      console.error("fetchPendingTransfer:", e);
    } finally {
      setPendingLoading(false);
    }
  }, [backendUrl, memorialId]);

  useEffect(() => {
    fetchPendingTransfer();
  }, [fetchPendingTransfer]);

  const formatExpiryLabel = (transfer) => {
    if (!transfer?.expiresAt) return "";
    const msLeft = new Date(transfer.expiresAt).getTime() - Date.now();
    const daysLeft = Math.max(0, Math.ceil(msLeft / (1000 * 60 * 60 * 24)));
    if (daysLeft === 0) return "Expires today";
    if (daysLeft === 1) return "Expires in 1 day";
    return `Expires in ${daysLeft} days`;
  };

  const handleCancelPending = async () => {
    if (!pendingTransfer?._id) return;
    setCancelLoading(true);
    try {
      const res = await authFetch(`${backendUrl}/api/transfers/${pendingTransfer._id}`, {
        method: "DELETE",
      });
      if (res.ok) {
        toast.success("Transfer invitation cancelled");
        setPendingTransfer(null);
      } else {
        const data = await res.json();
        toast.error(data.error || "Could not cancel transfer");
      }
    } catch (e) {
      toast.error("Could not cancel transfer");
    } finally {
      setCancelLoading(false);
    }
  };

  const handleTransfer = async () => {
    if (!transferEmail.trim()) {
      toast.error("Enter recipient email");
      return;
    }
    setTransferLoading(true);
    try {
      const res = await authFetch(`${backendUrl}/api/memorials/${memorialId}/transfer`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ toEmail: transferEmail.trim() }),
      });
      const data = await res.json();
      if (res.ok) {
        if (data.awaitingSignup) {
          toast.success(
            "Invitation sent. They'll receive an email to create an account and accept the transfer."
          );
        } else {
          toast.success("Transfer invitation sent");
        }
        setTransferEmail("");
        setPendingTransfer(data.transfer || null);
        await fetchPendingTransfer();
      } else {
        toast.error(data.error || "Transfer failed");
      }
    } catch (e) {
      toast.error("Transfer failed");
    } finally {
      setTransferLoading(false);
    }
  };

  return (
    <Card Width="75vw">
      <p className="dialog-header">Transfer ownership</p>
      <div className="dialog-content">
        <p style={{ marginBottom: 12 }}>
          Transfer admin access for {memorial?.firstName}&apos;s memorial to another person by email.
          If they don&apos;t have an account yet, we&apos;ll invite them to join WillAlwaysLoveU.
          The memorial plan and payment history stay with the original purchaser.
        </p>
        <p style={{ marginBottom: 16, fontWeight: 600, color: "#a04444" }}>
          You will lose admin access once they accept.
        </p>

        {pendingLoading ? (
          <p style={{ marginBottom: 16, color: "#6b6578" }}>Checking pending invitations…</p>
        ) : pendingTransfer ? (
          <div
            style={{
              marginBottom: 20,
              padding: "14px 16px",
              borderRadius: 8,
              border: "1px solid #d8d0e8",
              background: "#f8f6fb",
            }}
          >
            <p style={{ margin: "0 0 8px", fontWeight: 600, color: "#5c5470" }}>
              Pending transfer invitation
            </p>
            <p style={{ margin: "0 0 4px", color: "#5c5470" }}>
              Waiting for <strong>{pendingTransfer.toEmail}</strong>
              {!pendingTransfer.toUserId && " to create an account"}
            </p>
            <p style={{ margin: "0 0 12px", fontSize: "0.9rem", color: "#6b6578" }}>
              {formatExpiryLabel(pendingTransfer)}
              {!pendingTransfer.toUserId && " · Cancelled automatically if they don't sign up in 3 days"}
            </p>
            <Button
              text="Cancel invitation"
              isLoading={cancelLoading}
              onClick={handleCancelPending}
              bgColor="#a04444"
            />
          </div>
        ) : (
          <>
            <input
              type="email"
              className="title-input"
              placeholder="Recipient email address"
              value={transferEmail}
              onChange={(e) => setTransferEmail(e.target.value)}
              style={{ marginBottom: 12, width: "100%", maxWidth: 400 }}
            />
            <Button
              text="Send invitation"
              isLoading={transferLoading}
              onClick={handleTransfer}
              bgColor={textColor}
            />
          </>
        )}
      </div>
    </Card>
  );
}
